import React, { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Button } from "../../ui/button";
import { Switch } from "../../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Award, Loader2, Plus, Trash2, X } from "lucide-react";
import { VariavelWiredSelect } from "../../VariavelWiredSelect";
import {
  WIRED_NIVEL_MODOS,
  WiredConexao,
  WiredNivel,
  WiredNivelInfo,
  WiredNivelModo,
  WiredNivelPayload,
  wiredService,
} from "../../../services/wired";

interface WiredNivelModalProps {
  isOpen: boolean;
  onClose: () => void;
  nivel: WiredNivel | null;
  conexoes: WiredConexao[];
  onSuccess?: () => void;
}

interface RecompensaForm {
  id?: string;
  rotulo: string;
  nome_variavel: string;
  valor: string;
  a_cada: string;
  a_partir_de: string;
  teto_valor: string;
  id_conexao: string | null;
  pagar_retroativo: boolean;
}

const NOME_VARIAVEL_REGEX = /^[\w.-]{1,128}$/;
const INTEIRO_POSITIVO_REGEX = /^[1-9]\d{0,17}$/;
const EXEMPLO_MANUAL = "5=100\n10=500\n20=4000";

export function WiredNivelModal({ isOpen, onClose, nivel, conexoes, onSuccess }: WiredNivelModalProps) {
  const [submitting, setSubmitting] = useState(false);
  const [idConexao, setIdConexao] = useState("");
  const [nome, setNome] = useState("");
  const [variavelXp, setVariavelXp] = useState("");
  const [modo, setModo] = useState<WiredNivelModo>("exponencial");
  const [passo, setPasso] = useState("100");
  const [xpBase, setXpBase] = useState("30");
  const [fator, setFator] = useState("5");
  const [nivelMaximo, setNivelMaximo] = useState("50");
  const [entradaManual, setEntradaManual] = useState(EXEMPLO_MANUAL);
  const [recompensas, setRecompensas] = useState<RecompensaForm[]>([]);
  const [retroativo, setRetroativo] = useState(false);
  const [ativo, setAtivo] = useState(true);
  const [error, setError] = useState("");
  const [variaveis, setVariaveis] = useState<string[]>([]);
  const [xpSimulado, setXpSimulado] = useState("0");
  const [simulacao, setSimulacao] = useState<WiredNivelInfo | null>(null);
  const [simulando, setSimulando] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setIdConexao(nivel?.id_conexao ?? conexoes[0]?.id ?? "");
    setNome(nivel?.nome ?? "");
    setVariavelXp(nivel?.nome_variavel_xp ?? "");
    setModo(nivel?.modo ?? "exponencial");
    setPasso(String(nivel?.passo ?? 100));
    setXpBase(String(nivel?.xp_base ?? 30));
    setFator(String(nivel?.fator ?? 5));
    setNivelMaximo(String(nivel?.nivel_maximo ?? 50));
    setEntradaManual(nivel?.entrada_manual ?? EXEMPLO_MANUAL);
    setRecompensas(
      (nivel?.recompensas ?? []).map((r) => ({
        id: r.id,
        rotulo: r.rotulo,
        nome_variavel: r.nome_variavel,
        valor: r.valor,
        a_cada: String(r.a_cada),
        a_partir_de: String(r.a_partir_de),
        teto_valor: r.teto_valor ?? "",
        id_conexao: r.id_conexao ?? null,
        pagar_retroativo: r.pagar_retroativo ?? false,
      }))
    );
    setRetroativo(nivel?.retroativo ?? false);
    setAtivo(nivel?.ativo ?? true);
    setSimulacao(null);
    setError("");
  }, [isOpen, nivel, conexoes]);

  useEffect(() => {
    if (!isOpen || !idConexao) {
      setVariaveis([]);
      return;
    }
    let cancelado = false;
    wiredService
      .listarVariaveisConexao(idConexao)
      .then((res) => {
        if (!cancelado) setVariaveis(res.usuario ?? []);
      })
      .catch(() => {
        if (!cancelado) setVariaveis([]);
      });
    return () => {
      cancelado = true;
    };
  }, [isOpen, idConexao]);

  const mudar = (setter: (v: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setter(e.target.value);
    setError("");
    setSimulacao(null);
  };

  const parametros = () => ({
    modo,
    passo: modo === "linear" ? Number(passo) : null,
    xp_base: modo === "exponencial" ? Number(xpBase) : null,
    fator: modo === "exponencial" ? Number(fator.replace(",", ".")) : null,
    nivel_maximo: modo === "manual" ? null : Number(nivelMaximo),
    entrada_manual: modo === "manual" ? entradaManual : null,
  });

  const validar = (): string => {
    if (!idConexao) return "Selecione uma conexão.";
    if (!nome.trim() || nome.trim().length > 60) return "Nome é obrigatório (máximo 60 caracteres).";
    if (!NOME_VARIAVEL_REGEX.test(variavelXp.trim())) return "Variável de XP inválida (letras, números, _, - e .).";
    for (let i = 0; i < recompensas.length; i++) {
      const r = recompensas[i];
      const p = `Recompensa ${i + 1}`;
      if (!r.rotulo.trim() || r.rotulo.trim().length > 60) return `${p}: rótulo é obrigatório (máximo 60 caracteres).`;
      if (!NOME_VARIAVEL_REGEX.test(r.nome_variavel.trim())) return `${p}: variável inválida.`;
      if (!r.id_conexao && r.nome_variavel.trim() === variavelXp.trim()) return `${p}: não pode alterar a própria variável de XP.`;
      if (!INTEIRO_POSITIVO_REGEX.test(r.valor.trim())) return `${p}: o valor deve ser um inteiro maior que 0.`;
      const cada = Number(r.a_cada);
      if (!Number.isInteger(cada) || cada < 1) return `${p}: "a cada" deve ser um inteiro maior que 0.`;
      const desde = Number(r.a_partir_de);
      if (!Number.isInteger(desde) || desde < 2) return `${p}: "a partir do nível" deve ser no mínimo 2.`;
      if (r.teto_valor.trim()) {
        if (!INTEIRO_POSITIVO_REGEX.test(r.teto_valor.trim())) return `${p}: o teto deve ser um inteiro maior que 0.`;
        if (BigInt(r.teto_valor.trim()) < BigInt(r.valor.trim())) return `${p}: o teto não pode ser menor que o valor.`;
      }
    }
    return "";
  };

  const handleSimular = async () => {
    if (!/^\d{1,15}$/.test(xpSimulado.trim())) {
      setError("XP da simulação deve ser um inteiro maior ou igual a 0.");
      return;
    }
    try {
      setSimulando(true);
      setSimulacao(await wiredService.simularNivel({ ...parametros(), xp: xpSimulado.trim() } as any));
      setError("");
    } catch (err: any) {
      setSimulacao(null);
      setError(err.response?.data?.message || "Erro ao simular");
    } finally {
      setSimulando(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const mensagem = validar();
    if (mensagem) {
      setError(mensagem);
      return;
    }

    const payload = {
      id_conexao: idConexao,
      nome: nome.trim(),
      nome_variavel_xp: variavelXp.trim(),
      ...parametros(),
      recompensas: recompensas.map((r) => ({
        ...(r.id ? { id: r.id } : {}),
        rotulo: r.rotulo.trim(),
        nome_variavel: r.nome_variavel.trim(),
        valor: r.valor.trim(),
        a_cada: Number(r.a_cada),
        a_partir_de: Number(r.a_partir_de),
        teto_valor: r.teto_valor.trim() || null,
        id_conexao: r.id_conexao && r.id_conexao !== idConexao ? r.id_conexao : null,
        pagar_retroativo: r.pagar_retroativo,
      })),
      retroativo,
      ativo,
    } as WiredNivelPayload;

    try {
      setSubmitting(true);
      if (nivel) {
        await wiredService.atualizarNivel(nivel.id, payload);
        enqueueSnackbar("Sistema de nível atualizado!", { variant: "success" });
      } else {
        await wiredService.criarNivel(payload);
        enqueueSnackbar("Sistema de nível criado!", { variant: "success" });
      }
      onClose();
      if (onSuccess) onSuccess();
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.message || "Erro ao salvar sistema de nível", { variant: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const num = (v: string, set: (v: string) => void, label: string) => (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Input value={v} onChange={mudar(set)} inputMode="decimal" className="font-mono" disabled={submitting} />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <Card className="w-full max-w-xl mx-4 max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Award className="h-5 w-5 text-[oklch(0.45_0.15_142)]" />
              {nivel ? "Editar sistema de nível" : "Novo sistema de nível"}
            </CardTitle>
            <CardDescription>Mesma lógica do extra "Sistema de Aumento de Nível" do Wired</CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-6 w-6 p-0" disabled={submitting}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <datalist id="nivel_variaveis_lista">
              {variaveis.slice(0, 500).map((v) => (
                <option key={v} value={v} />
              ))}
            </datalist>

            <div className="space-y-2">
              <Label>Conexão</Label>
              <Select value={idConexao} onValueChange={setIdConexao} disabled={submitting}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione uma conexão" />
                </SelectTrigger>
                <SelectContent>
                  {conexoes.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.nome} (quarto {c.room_id}){!c.ativo ? " - inativa" : ""}
                      {!c.possui_chave_escrita ? " - sem chave de escrita" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nivel_nome">Nome</Label>
                <Input id="nivel_nome" value={nome} onChange={mudar(setNome)} placeholder="Ex: Sobrevivência" disabled={submitting} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="nivel_xp">Variável de XP</Label>
                <Input
                  id="nivel_xp"
                  list="nivel_variaveis_lista"
                  value={variavelXp}
                  onChange={mudar(setVariavelXp)}
                  placeholder="Ex: exp"
                  className="font-mono"
                  autoComplete="off"
                  disabled={submitting}
                />
              </div>
            </div>

            <div className="space-y-3 rounded-lg border border-border p-3">
              <Label>Modo de progressão</Label>
              <Select
                value={modo}
                onValueChange={(v) => {
                  setModo(v as WiredNivelModo);
                  setSimulacao(null);
                }}
                disabled={submitting}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {WIRED_NIVEL_MODOS.map((m: any) => (
                    <SelectItem key={m.value ?? m.valor} value={m.value ?? m.valor}>
                      {m.label ?? m.rotulo}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {modo === "linear" && (
                <div className="grid grid-cols-2 gap-3">
                  {num(passo, setPasso, "Tamanho da etapa (XP por nível)")}
                  {num(nivelMaximo, setNivelMaximo, "Nível máximo")}
                </div>
              )}

              {modo === "exponencial" && (
                <div className="grid grid-cols-3 gap-3">
                  {num(xpBase, setXpBase, "XP base")}
                  {num(fator, setFator, "Fator de aumento (%)")}
                  {num(nivelMaximo, setNivelMaximo, "Nível máximo")}
                </div>
              )}

              {modo === "manual" && (
                <div className="space-y-1">
                  <Label className="text-xs">Uma linha por ponto: nível=XP total (o nível 1 começa em 0 XP)</Label>
                  <textarea
                    value={entradaManual}
                    onChange={(e) => {
                      setEntradaManual(e.target.value);
                      setError("");
                      setSimulacao(null);
                    }}
                    rows={5}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-sm"
                    disabled={submitting}
                  />
                </div>
              )}

              <div className="flex flex-wrap items-end gap-2 border-t border-border pt-3">
                <div className="space-y-1">
                  <Label className="text-xs">Simular com XP</Label>
                  <Input
                    value={xpSimulado}
                    onChange={(e) => setXpSimulado(e.target.value)}
                    inputMode="numeric"
                    className="w-32 font-mono"
                    disabled={submitting}
                  />
                </div>
                <Button type="button" variant="outline" size="sm" onClick={handleSimular} disabled={submitting || simulando}>
                  {simulando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simular"}
                </Button>
                {simulacao && (
                  <p className="text-xs text-muted-foreground">
                    Nível <span className="font-semibold text-foreground">{simulacao.nivel}</span> de {simulacao.nivel_maximo} ·{" "}
                    {simulacao.no_maximo
                      ? "máximo"
                      : `${simulacao.progresso}/${simulacao.xp_necessario} XP (${simulacao.percentual}%)`}
                    {" · "}XP máx. {simulacao.xp_maximo}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-3 rounded-lg border border-border p-4">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <Label>Recompensas por nível</Label>
                  <p className="text-xs text-muted-foreground">
                    A cada X níveis, soma o valor à variável (ex.: +2 pontos de atributo a cada 5 níveis).
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={submitting || recompensas.length >= 10}
                  onClick={() =>
                    setRecompensas((atual) => [
                      ...atual,
                      { rotulo: "", nome_variavel: "", valor: "1", a_cada: "1", a_partir_de: "2", teto_valor: "", id_conexao: null, pagar_retroativo: false },
                    ])
                  }
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Adicionar
                </Button>
              </div>

              {recompensas.map((r, i) => {
                const alterar = (parcial: Partial<RecompensaForm>) => {
                  setRecompensas((atual) => atual.map((x, j) => (j === i ? { ...x, ...parcial } : x)));
                  setError("");
                };
                return (
                  <div key={i} className="grid grid-cols-2 sm:grid-cols-6 gap-2 items-end border-t border-border pt-3 first:border-t-0 first:pt-0">
                    <div className="space-y-1 sm:col-span-2">
                      <Label className="text-xs">Rótulo</Label>
                      <Input value={r.rotulo} onChange={(ev) => alterar({ rotulo: ev.target.value })} placeholder="Ex: Pontos de atributo" disabled={submitting} />
                    </div>
                    <div className="space-y-1 sm:col-span-2">
                      <Label className="text-xs">Conexão</Label>
                      <Select
                        value={r.id_conexao ?? idConexao}
                        onValueChange={(v) => alterar({ id_conexao: v === idConexao ? null : v, nome_variavel: "" })}
                        disabled={submitting}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {conexoes.map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                              {c.nome}
                              {!c.possui_chave_escrita ? " - sem chave de escrita" : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1 sm:col-span-2">
                      <Label className="text-xs">Variável</Label>
                      <VariavelWiredSelect
                        value={r.nome_variavel}
                        onChange={(v) => alterar({ nome_variavel: v })}
                        idConexao={r.id_conexao ?? idConexao}
                        ativo={isOpen}
                        disabled={submitting}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Soma</Label>
                      <Input value={r.valor} onChange={(ev) => alterar({ valor: ev.target.value })} inputMode="numeric" className="font-mono" disabled={submitting} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">A cada (níveis)</Label>
                      <Input value={r.a_cada} onChange={(ev) => alterar({ a_cada: ev.target.value })} inputMode="numeric" className="font-mono" disabled={submitting} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">A partir do nível</Label>
                      <Input value={r.a_partir_de} onChange={(ev) => alterar({ a_partir_de: ev.target.value })} inputMode="numeric" className="font-mono" disabled={submitting} />
                    </div>
                    <div className="space-y-1 sm:col-span-2">
                      <Label className="text-xs">Teto (opcional)</Label>
                      <Input value={r.teto_valor} onChange={(ev) => alterar({ teto_valor: ev.target.value })} inputMode="numeric" className="font-mono" disabled={submitting} />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="text-red-600"
                      disabled={submitting}
                      onClick={() => setRecompensas((atual) => atual.filter((_, j) => j !== i))}
                      aria-label="Remover recompensa"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    {!r.id && (
                      <div className="col-span-2 sm:col-span-6 flex items-center gap-2">
                        <Switch checked={r.pagar_retroativo} onCheckedChange={(v) => alterar({ pagar_retroativo: v })} disabled={submitting} />
                        <Label className="text-xs">Pagar também os níveis anteriores já alcançados (só esta recompensa, uma vez por militar)</Label>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="flex items-center gap-3">
              <Switch checked={ativo} onCheckedChange={setAtivo} disabled={submitting} />
              <Label>Ativo</Label>
            </div>

            <div className="flex items-start gap-3 rounded-lg border border-border p-3">
              <Switch checked={retroativo} onCheckedChange={setRetroativo} disabled={submitting} />
              <div>
                <Label>Pagar níveis já alcançados</Label>
                <p className="text-xs text-muted-foreground">
                  Desligado: cada militar só recebe recompensas a partir do nível em que estiver quando o sistema o vir pela
                  primeira vez. Ligado: recebe também as dos níveis que já tem.
                </p>
              </div>
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
                Cancelar
              </Button>
              <Button type="submit" disabled={submitting} className="bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
